import { useLanguageData } from '@/common/hooks/useLanguageData';
import { formatTranslation } from '@/common/utils/formatTranslation';
import { OPINION_DECISION, OPINION_SOURCE } from '../../constants/opinion.constants';
import useChatFeedback from '../../hooks/useChatFeedback';
import {
	CompanionActions,
	CompanionActionButton,
} from '../chatCompanion/chatCompanion.styles';
import ChatFeedbackModal from './ChatFeedbackModal';
import { BaseSVG, IconButton, Wrapper } from './ChatFeedback.styles';

const ThumbUpIcon = (props) => (
	<BaseSVG width="20" height="20" stroke="currentColor" {...props}>
		<path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
	</BaseSVG>
);

const ThumbDownIcon = (props) => (
	<BaseSVG width="20" height="20" stroke="currentColor" {...props}>
		<path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-11h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
	</BaseSVG>
);

const ThumbUpCompactIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="M7 11v10H4a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h3Z" />
		<path d="M7 11l4.3-8.2A2 2 0 0 1 15 4v5h5a2 2 0 0 1 2 2.3l-1.2 7A3 3 0 0 1 17.9 21H7V11Z" />
	</svg>
);

const ThumbDownCompactIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path d="M17 13V3h3a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-3Z" />
		<path d="M17 13l-4.3 8.2A2 2 0 0 1 9 20v-5H4a2 2 0 0 1-2-2.3l1.2-7A3 3 0 0 1 6.1 3H17v10Z" />
	</svg>
);

const ProfileInfoIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<circle cx="12" cy="8" r="4" />
		<path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
	</svg>
);

const ChatFeedback = ({ person, variant = 'default', onInfoClick }) => {
	const nav = useLanguageData('chat');
	const isCompanion = variant === 'companion';
	const {
		t,
		activeDecision,
		text,
		setText,
		selectedDecision,
		handleIconClick,
		cancelFeedback,
		skipFeedback,
		submitFeedback,
		isSubmitDisabled,
		isSubmitting,
	} = useChatFeedback(person, { source: OPINION_SOURCE.PERSON });

	const actionButtons = isCompanion ? (
		<CompanionActions aria-label={t.feedbackActionsLabel}>
			<CompanionActionButton
				type="button"
				$info
				onClick={onInfoClick}
				aria-label={formatTranslation(t.profileInfoAria, { name: person?.name || '' })}
			>
				<ProfileInfoIcon />
			</CompanionActionButton>
			<CompanionActionButton
				type="button"
				$decision={OPINION_DECISION.HAND_UP}
				$active={selectedDecision === OPINION_DECISION.HAND_UP}
				onClick={() => handleIconClick(OPINION_DECISION.HAND_UP)}
				aria-label={t.feedbackLikeTitle}
			>
				<ThumbUpCompactIcon />
			</CompanionActionButton>
			<CompanionActionButton
				type="button"
				$decision={OPINION_DECISION.HAND_DOWN}
				$active={selectedDecision === OPINION_DECISION.HAND_DOWN}
				onClick={() => handleIconClick(OPINION_DECISION.HAND_DOWN)}
				aria-label={t.feedbackDislikeTitle}
			>
				<ThumbDownCompactIcon />
			</CompanionActionButton>
		</CompanionActions>
	) : (
		<Wrapper>
			<IconButton
				$decision={OPINION_DECISION.HAND_UP}
				$isActive={selectedDecision === OPINION_DECISION.HAND_UP}
				onClick={() => handleIconClick(OPINION_DECISION.HAND_UP)}
				aria-label={nav.feedbackLikeTitle}
			>
				<ThumbUpIcon />
			</IconButton>
			<IconButton
				$decision={OPINION_DECISION.HAND_DOWN}
				$isActive={selectedDecision === OPINION_DECISION.HAND_DOWN}
				onClick={() => handleIconClick(OPINION_DECISION.HAND_DOWN)}
				aria-label={nav.feedbackDislikeTitle}
			>
				<ThumbDownIcon />
			</IconButton>
		</Wrapper>
	);

	return (
		<>
			{actionButtons}
			<ChatFeedbackModal
				activeDecision={activeDecision}
				text={text}
				setText={setText}
				cancelFeedback={cancelFeedback}
				skipFeedback={skipFeedback}
				submitFeedback={submitFeedback}
				isSubmitDisabled={isSubmitDisabled}
				isSubmitting={isSubmitting}
				t={t}
			/>
		</>
	);
};

export default ChatFeedback;
